import { useEditor, EditorContent } from "@tiptap/react";
import Placeholder from "@tiptap/extension-placeholder";
import { useState } from "react";
import { documentExtensions } from "@/lib/document-extensions";
import { PLACEHOLDERS } from "@/lib/documents/placeholders";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  TextB,
  TextItalic,
  TextUnderline as UnderlineIcon,
  TextHTwo,
  TextHThree,
  List,
  ListNumbers,
  Quotes,
  Minus,
  ArrowCounterClockwise,
  ArrowClockwise,
} from "@phosphor-icons/react";
import "@/components/blog/editor.css";

interface DocumentEditorProps {
  initialContent?: object;
  onChange: (content: object) => void;
}

export function DocumentEditor({ initialContent, onChange }: DocumentEditorProps) {
  const [fieldsOpen, setFieldsOpen] = useState(false);

  const editor = useEditor({
    extensions: [
      ...documentExtensions,
      Placeholder.configure({ placeholder: "Počnite pisati dokument..." }),
    ],
    content: initialContent || undefined,
    onUpdate: ({ editor }) => {
      onChange(editor.getJSON());
    },
    editorProps: {
      attributes: {
        class: "prose prose-lg max-w-none focus:outline-none",
      },
    },
  });

  if (!editor) return null;

  const insertField = (key: string) => {
    editor.chain().focus().insertContent({ type: "placeholder", attrs: { key } }).run();
    setFieldsOpen(false);
  };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden focus-within:ring-2 focus-within:ring-ring/30 transition-shadow">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/30 px-2 py-1.5">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Bold"
        >
          <TextB className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Italic"
        >
          <TextItalic className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive("underline")}
          title="Underline"
        >
          <UnderlineIcon className="size-4" />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
          title="Heading 2"
        >
          <TextHTwo className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}
          title="Heading 3"
        >
          <TextHThree className="size-4" />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Bullet List"
        >
          <List className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          title="Numbered List"
        >
          <ListNumbers className="size-4" />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
          title="Blockquote"
        >
          <Quotes className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          title="Divider"
        >
          <Minus className="size-4" />
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo"
        >
          <ArrowCounterClockwise className="size-4" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo"
        >
          <ArrowClockwise className="size-4" />
        </ToolbarButton>

        <ToolbarDivider />

        <Popover open={fieldsOpen} onOpenChange={setFieldsOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" size="sm">
              Ubaci polje
            </Button>
          </PopoverTrigger>
          <PopoverContent className="max-h-80 w-72 overflow-y-auto p-1">
            {PLACEHOLDERS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => insertField(p.key)}
                className="flex w-full flex-col items-start rounded px-2 py-1.5 text-left hover:bg-muted"
              >
                <span className="text-sm">{p.label}</span>
                <span className="text-xs text-muted-foreground">{p.key}</span>
              </button>
            ))}
          </PopoverContent>
        </Popover>
      </div>

      {/* Editor content */}
      <EditorContent editor={editor} />
    </div>
  );
}

function ToolbarButton({
  children,
  active,
  disabled,
  onClick,
  title,
}: {
  children: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "inline-flex items-center justify-center rounded p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:pointer-events-none",
        active && "bg-accent text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="mx-1 h-5 w-px bg-border" />;
}
